// https://github.com/azmenak/material-ui-nested-menu-item
import React, { useState, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import RootRef from '@material-ui/core/RootRef';
import classnames from 'classnames';
import { get } from 'lodash';

import Menu from './Menu';
import MenuItem from './MenuItem';

import Box from '../../Styled/Box';
import Icon from '../../Icons/Icon';
import Typography from '../../Typography/Typography';

const TRANSPARENT = 'rgba(0,0,0,0)';

const useMenuItemStyles = makeStyles((theme) => ({
  root: (props) => ({
    backgroundColor: props.open ? theme.palette.action.hover : TRANSPARENT,
  }),
}));

const anchorOrigins = {
  ltr: {
    vertical: 'top',
    horizontal: 'right',
  },
  rtl: {
    vertical: 'top',
    horizontal: 'left',
  },
};

const transformOrigins = {
  ltr: {
    vertical: 'top',
    horizontal: 'left',
  },
  rtl: {
    vertical: 'top',
    horizontal: 'right',
  },
};

/**
 * Use as a drop-in replacement for `<MenuItem>` when you need to add cascading
 * menu elements as children to this component.
 */
const NestedMenuItem = React.forwardRef((props, ref) => {
  const {
    parentMenuOpen,
    component,
    label,
    children,
    className,
    tabIndex: tabIndexProp,
    MenuProps,
    ContainerProps: ContainerPropsProp = {},
    disabled,
    direction,
    ...MenuItemProps
  } = props;

  const { ref: containerRefProp, ...ContainerProps } = ContainerPropsProp;

  const menuItemRef = useRef(null);
  useImperativeHandle(ref, () => menuItemRef.current);

  const containerRef = useRef(null);
  useImperativeHandle(containerRefProp, () => containerRef.current);

  const menuContainerRef = useRef(null);

  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);

  const handleMouseEnter = (event) => {
    setIsSubMenuOpen(true);

    if (ContainerProps.onMouseEnter) {
      ContainerProps.onMouseEnter(event);
    }
  };
  const handleMouseLeave = (event) => {
    setIsSubMenuOpen(false);

    if (ContainerProps && ContainerProps.onMouseLeave) {
      ContainerProps.onMouseLeave(event);
    }
  };

  // Check if any immediate children are active
  const isSubmenuFocused = () => {
    const active = get(containerRef.current, 'ownerDocument.activeElement');
    const _children = get(menuContainerRef.current, 'children', []);
    return _children.find((d) => d === active);
  };

  const handleFocus = (event) => {
    if (event.target === containerRef.current) {
      setIsSubMenuOpen(true);
    }

    if (ContainerProps.onFocus) {
      ContainerProps.onFocus(event);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      return;
    }

    if (isSubmenuFocused()) {
      event.stopPropagation();
    }

    const active = get(containerRef.current, 'ownerDocument.activeElement');

    if (event.key === 'ArrowLeft' && isSubmenuFocused()) {
      if (containerRef.current) {
        containerRef.current.focus();
      }
    }

    if (
      event.key === 'ArrowRight'
      && event.target === containerRef.current
      && event.target === active
    ) {
      const firstChild = get(menuContainerRef.current, 'children.0');

      if (firstChild) {
        firstChild.focus();
      }
    }
  };

  const open = isSubMenuOpen && parentMenuOpen;
  const menuItemClasses = useMenuItemStyles({ open });

  // Root element must have a `tabIndex` attribute for keyboard navigation
  let tabIndex;

  if (!disabled) {
    tabIndex = tabIndexProp !== undefined ? tabIndexProp : -1;
  }

  return (
    <div
      {...ContainerProps}
      role='listbox'
      ref={containerRef}
      onFocus={handleFocus}
      tabIndex={tabIndex}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={handleKeyDown}
    >
      <RootRef rootRef={menuItemRef}>
        <MenuItem
          {...MenuItemProps}
          className={classnames(menuItemClasses.root, className)}
        >
          <Box
            width='100%'
            display='flex'
            alignItems='center'
            justifyContent={direction === 'ltr' ? 'space-between' : 'flex-start'}
          >
            {direction.toLowerCase() === 'rtl' && (
              <Icon
                icon='chevron-left'
                color='coldgrey500'
                mr={3}
              />
            )}

            <Typography variant='helper'>
              {label}
            </Typography>

            {direction.toLowerCase() === 'ltr' && (
              <Icon
                icon='chevron-right'
                color='coldgrey500'
                ml={3}
              />
            )}
          </Box>
        </MenuItem>
      </RootRef>

      <Menu
        // Set pointer events to 'none' to prevent the invisible Popover div
        // from capturing events for clicks and hovers
        getContentAnchorEl={null}
        style={{ pointerEvents: 'none' }}
        anchorEl={menuItemRef.current}
        anchorOrigin={anchorOrigins[direction] || anchorOrigins.ltr}
        transformOrigin={transformOrigins[direction] || transformOrigins.ltr}
        open={open}
        autoFocus={false}
        disableAutoFocus
        disableEnforceFocus
        onClose={() => {
          setIsSubMenuOpen(false);
        }}
      >
        <div ref={menuContainerRef} style={{ pointerEvents: 'auto' }}>
          {children}
        </div>
      </Menu>
    </div>
  );
});

NestedMenuItem.propTypes = {
  parentMenuOpen: PropTypes.bool,
  component: PropTypes.string,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string,
  children: PropTypes.any,
  className: PropTypes.string,
  tabIndex: PropTypes.number,
  MenuProps: PropTypes.object,
  ContainerProps: PropTypes.object,
  disabled: PropTypes.bool,
  direction: PropTypes.string,
};

NestedMenuItem.defaultProps = {
  parentMenuOpen: false,
  component: 'div',
  icon: 'chevron-right',
  children: [],
  className: '',
  tabIndex: 0,
  MenuProps: {},
  ContainerProps: {},
  disabled: false,
  direction: 'ltr',
};

export default NestedMenuItem;
